import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { AccountHeader, CompleteProfileNotice, ProfileCard, SecurityCard, SignInMethodsCard, QuickLinks } from '@/components/account/AccountSections';
import { UaePassCard, DocumentsCard } from '@/components/account/IdentityCards';
import { BRAND_ACCOUNT } from '@/api/account';

const FLASH = { linked: 'UAE PASS linked to your account.', 'signed-in': 'Signed in with UAE PASS.' };

export default function AccountPage() {
  const { customer, ready, setCustomer, logout, refresh } = useCustomerAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  useEffect(() => { document.title = `My Account | ${BRAND_ACCOUNT}`; }, []);

  useEffect(() => {
    const flash = params.get('uaepass');
    const error = params.get('error');
    if (!flash && !error) return;
    if (flash) { toast.success(FLASH[flash] || 'Done.'); refresh(); }
    if (error) toast.error(error);
    setParams({}, { replace: true });
  }, [params, setParams, refresh]);

  if (!ready) return <div data-testid="account-loading" className="min-h-[50vh] grid place-items-center"><Loader2 className="w-7 h-7 text-[#EE5A01] animate-spin" /></div>;
  if (!customer) return <Navigate to="/login" replace state={{ from: '/account' }} />;

  const handleLogout = async () => {
    const res = await logout();
    if (res?.uaepass_logout_url) { window.location.href = res.uaepass_logout_url; return; }
    navigate('/login', { replace: true, state: { notice: 'You have been signed out.' } });
  };

  return (
    <div data-testid="account-page" className="bg-[#F5F5F3]">
      <AccountHeader customer={customer} onLogout={handleLogout} isNew={Boolean(location.state?.isNew)} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-6">
        {!customer.mobile && <CompleteProfileNotice />}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <UaePassCard customer={customer} onChanged={setCustomer} />
            <ProfileCard key={customer.updated_at || customer.user_id} customer={customer} onSaved={setCustomer} />
            <DocumentsCard customer={customer} />
            <SecurityCard customer={customer} onSaved={setCustomer} />
          </div>
          <div className="space-y-6">
            <SignInMethodsCard customer={customer} />
            <QuickLinks />
          </div>
        </div>
      </div>
    </div>
  );
}
